import { useQuery } from "@tanstack/react-query";
import type { MapsCallSource } from "~/lib/maps/attribution";
import {
  getPlaceDetails,
  PlaceDetails,
} from "~/lib/server-functions/get-place-detail";
import { QUERY_KEYS } from "./queryKeys";

export type UsePlaceDetailsOptions = {
  /** Callsite for cost attribution; required so every call is attributable. */
  source: MapsCallSource;
  sessionToken?: string;
  /**
   * Gate the query. Use this to keep Place Details off the wire until the
   * feature that needs it is actually visible (e.g. the search map).
   */
  enabled?: boolean;
};

export const buildPlaceDetailsQueryOptions = (
  placeId: string,
  options: UsePlaceDetailsOptions,
) => ({
  // Include sessionToken so a selection with a billing session does not reuse a
  // cached no-token response (which would skip the Google session-close call).
  queryKey: [QUERY_KEYS.PLACE_DETAILS, placeId, options.sessionToken ?? ""],
  queryFn: async (): Promise<PlaceDetails | null> => {
    if (!placeId) return null;
    return await getPlaceDetails({
      data: {
        placeId,
        sessionToken: options.sessionToken,
        source: options.source,
      },
    });
  },
  enabled: Boolean(placeId) && (options.enabled ?? true),
  staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  // A failed Place Details request is still a billed request; never auto-retry.
  retry: false as const,
});

export const usePlaceDetails = (
  placeId: string,
  options: UsePlaceDetailsOptions,
) => {
  return useQuery<PlaceDetails | null>(
    buildPlaceDetailsQueryOptions(placeId, options),
  );
};
